import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from "@angular/core";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { FormControl } from "@angular/forms";
import { Observable, Subject, zip } from "rxjs";
import { ClipboardService } from "ngx-clipboard";
import { CommonSnackBarService } from "../../_services/common-snack-bar.service";
import { InspectionReportService } from "../../_services/http-api/inspection-report.service";
import { SpinnerService } from "../../_services/spinner.service";
import { DATE_PICKER_VALIDATION } from "../../app.constants";
import { CustomValidators } from "../../_shared/custom-validator";
import { InspectionReport } from "../../_objects/inspection-report";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { APP_DATE_FORMATS, AppDateAdapter } from "../../inspection-report/inspection-report-edit-text-dialog.component";
import { TenantUserList } from "../../_objects/user";
import { TranslateService } from "@ngx-translate/core";
import { LangUpdateService } from "../../_services/lang-update.service";
import { UsersStateService } from "../../_services/user-state/users-state.service";
import { getExpirationDate, getFormatedExpirationDate } from "../../_utils/date.utils";

@Component({
  selector: "app-common-share-dialog",
  templateUrl: "./common-share-dialog.component.html",
  styleUrls: ["./common-share-dialog.component.scss"],
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  standalone: false
})
export class CommonShareDialogComponent implements OnInit, OnDestroy {
  readonly minDate = new Date(DATE_PICKER_VALIDATION.MIN_YEAR, DATE_PICKER_VALIDATION.MIN_MONTH - 1, DATE_PICKER_VALIDATION.MIN_DAY);
  readonly maxDate = new Date(DATE_PICKER_VALIDATION.MAX_YEAR, DATE_PICKER_VALIDATION.MAX_MONTH - 1, DATE_PICKER_VALIDATION.MAX_DAY);
  copiedToClipboard: string = this.translate.instant("commonShareDialog.copiedToClipboard");
  sasKeyIssuanceFailure: string = this.translate.instant("commonShareDialog.sasKeyIssuanceFailure");
  reportSasKyeDescription: string = this.translate.instant("commonShareDialog.reportSasKyeDescription");
  mailSubject: string = this.translate.instant("commonShareDialog.mailSubject");
  public textSasKey: string;
  public createByName: string;
  public report: InspectionReport;
  public expirationDate: string;
  public oldExpirationDate: string;
  private onDestroy$ = new Subject();

  @Output() changedValue = new EventEmitter<number>();

  sharingDateForm = new FormControl<Date>({ value: getExpirationDate(), disabled: false }, [CustomValidators.requiredTrimmed()]);

  constructor(
    private clipboardApi: ClipboardService,
    private snackbar: CommonSnackBarService,
    private oscSpinnerService: SpinnerService,
    private usersStateService: UsersStateService,
    private inspectionReportService: InspectionReportService,
    @Inject(MAT_DIALOG_DATA) public data: { report: InspectionReport },
    private translate: TranslateService,
    private langService: LangUpdateService
  ) {
    this.langService.prepare(translate, this.onDestroy$, (res) => {
      this.copiedToClipboard = this.translate.instant("commonShareDialog.copiedToClipboard");
      this.sasKeyIssuanceFailure = this.translate.instant("commonShareDialog.sasKeyIssuanceFailure");
      this.reportSasKyeDescription = this.translate.instant("commonShareDialog.reportSasKyeDescription");
      this.mailSubject = this.translate.instant("commonShareDialog.mailSubject");
    });
    this.report = this.data.report;
  }

  ngOnInit(): void {
    this.expirationDate = getFormatedExpirationDate();
    this.oldExpirationDate = getFormatedExpirationDate();
    this.getReportdata();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
  }

  // Reportのデータを取得
  getReportdata() {
    const isDraft = this.report.status !== "900";
    const version = this.report.status && this.report.version ? this.report.version : null;

    this.oscSpinnerService.show(true);

    zip(
      this.usersStateService.users$,
      this.inspectionReportService.getInspectionReportSharedLink(this.report.reportId, this.expirationDate, version, isDraft)
    ).subscribe(
      (val) => {
        this.getCreateByName(val[0]);
        this.textSasKey = val[1].sasUri;
      },
      (error) => {
        console.log(error);
        this.snackbar.open(this.sasKeyIssuanceFailure);
        this.oscSpinnerService.hide(true);
      },
      () => this.oscSpinnerService.hide(true)
    );
  }

  // 作成者名を取得する
  getCreateByName(tenantUsers: TenantUserList[]) {
    const user = tenantUsers.find((_) => _.id === this.report.createdBy);
    this.createByName = user ? user.givenName + " " + user.surname : this.translate.instant("commonShareDialog.unknownUser");
  }

  // DetePickerで値を変更した際のイベント
  changeDateEvent(event: MatDatepickerInputEvent<Date>) {
    this.expirationDate = getFormatedExpirationDate();
    this.oldExpirationDate = getFormatedExpirationDate();
    this.getReportdata();
  }

  /** 表示するプレースホルダーを返却する
   * @returns string プレースホルダー
   */
  getPlaceholder(message?: string): Observable<string> | string {
    if (message) {
      return message;
    } else {
      return this.textSasKey;
    }
  }

  /** 表示するTooltipを返却する
   * @returns string ツールチップ
   */
  getTooltip(massage: string): string {
    return massage;
  }

  /** 表示するmailtoを返却する
   * @returns string mailto
   */
  getMailTo(): string {
    const mailto = "mailto:?";
    const subject = "subject=";
    const title = "body=" + this.translate.instant("commonShareDialog.getMailToReportName") + ": ";
    const name = "" + this.translate.instant("commonShareDialog.getMailToCreatedBy") + ": ";
    const date = "" + this.translate.instant("commonShareDialog.getMailToExpirationDate") + ": ";
    const and = "&";
    const newLine = "%0D%0A";

    return (
      mailto +
      subject +
      this.mailSubject +
      and +
      title +
      this.report.title +
      newLine +
      name +
      this.createByName +
      newLine +
      date +
      this.expirationDate +
      newLine +
      newLine +
      this.reportSasKyeDescription +
      newLine +
      encodeURIComponent(this.textSasKey)
    );
  }

  // クリップボードにコピー
  copyToClipboard() {
    this.clipboardApi.copyFromContent(this.textSasKey.toString());
    this.snackbar.open(this.copiedToClipboard);
  }
}
